/** @file Canonical Huffman decode tables (RFC 1951 §3.2.2) from code lengths: a 9-bit lookup plus counts. */

/**
 * One alphabet's decoder, rebuilt in place per block. `fast[next 9 input bits]` = symbol << 4 | code length for codes
 * of at most 9 bits (0: a longer code, or no code); `count[len]` and `symbol` (ordered by length, then value) let a
 * longer code be walked canonically. `next` is scratch.
 */
export interface HuffmanTable {
  fast: Uint16Array;
  count: Uint16Array;
  symbol: Uint16Array;
  next: Uint16Array;
}

const at = (a: Uint8Array | Uint16Array, i: number): number => a[i] ?? 0;

/** `code` (its `len` low bits) reversed: deflate packs Huffman codes most significant bit first. */
function reversed(code: number, len: number): number {
  let out = 0;
  for (let i = 0; i < len; i++) out |= ((code >>> i) & 1) << (len - 1 - i);
  return out;
}

/** Counts codes per length; false for an over-subscribed set, or an incomplete one unless allowed. */
function countLengths(t: HuffmanTable, lengths: Uint8Array, start: number, n: number, incompleteOk: boolean): boolean {
  const count = t.count;
  count.fill(0);
  for (let i = start; i < start + n; i++) count[at(lengths, i)] = at(count, at(lengths, i)) + 1;
  count[0] = 0;
  let left = 1;
  let max = 0;
  for (let len = 1; len <= 15; len++) {
    left = (left << 1) - at(count, len);
    if (left < 0) return false;
    max = at(count, len) > 0 ? len : max;
  }
  // An incomplete set is only legal for one code of one bit (or none), as RFC-conformant decoders treat it.
  return left === 0 || (incompleteOk && max <= 1);
}

/** Orders the used symbols by code length, then value (canonical order). */
function sortSymbols(t: HuffmanTable, lengths: Uint8Array, start: number, n: number): void {
  const { count, symbol, next } = t;
  next[1] = 0;
  for (let len = 1; len < 15; len++) next[len + 1] = at(next, len) + at(count, len);
  for (let i = 0; i < n; i++) {
    const len = at(lengths, start + i);
    if (len === 0) continue;
    const slot = at(next, len);
    symbol[slot] = i;
    next[len] = slot + 1;
  }
}

/** Fills the 9-bit lookup: each code of at most 9 bits, reversed, repeated over every value of the bits after it. */
function fillFast(t: HuffmanTable): void {
  const { count, symbol, fast } = t;
  fast.fill(0);
  let code = 0;
  let k = 0;
  for (let len = 1; len <= 9; len++, code <<= 1) {
    for (let j = 0; j < at(count, len); j++, k++, code++) {
      const entry = (at(symbol, k) << 4) | len;
      for (let f = reversed(code, len); f < 512; f += 1 << len) fast[f] = entry;
    }
  }
}

/**
 * Builds `t` from lengths[start, start + n) (0 = symbol unused, at most 15). False for an over-subscribed set and for an
 * incomplete one, except a single one-bit code or no code at all when `incompleteOk` (distance and literal trees).
 * Work: n + 15 + at most 512 table writes; nothing is allocated.
 */
export function buildHuffman(t: HuffmanTable, lengths: Uint8Array, start: number, n: number, incompleteOk: boolean): boolean {
  if (!countLengths(t, lengths, start, n, incompleteOk)) return false;
  sortSymbols(t, lengths, start, n);
  fillFast(t);
  return true;
}
